import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, Spin } from 'antd';
import axios from 'axios';
import './OrderList.css';
import OrderListHeader from './OrderListHeader';
import OrderModals from './OrderModals';
import BatchManagement from './BatchManagement';
import OrderListToolbar from './OrderListToolbar';
import SearchFunctions from './SearchFunctions';
import { columnStyle } from './OrderListStyles';
import VirtualizedOrderList from './VirtualizedOrderList';
import BatchOperations from './BatchOperations';
import DownloadModal from './DownloadModal';
import { formatDate, getStatusColor, getStatusLabel } from './OrderStatusUtils';
import { isStaffOrSuperuser } from '../../utils/authUtils';

const { Content } = Layout;

/**
 * OrderList component - displays a list of orders with virtualized rendering for performance
 */
const OrderList = ({ 
  orders = [], 
  loading, 
  pagination, 
  onPageChange, 
  onOrderClick, 
  onOrdersUpdate,
  onSearchResults,
  onClearSearch,
  isSearchMode,
  showBatchButtons,
  totalOrders,
  onChangeCustomer,
  airwayBillId,
  isMyOrdersRoute,
  isOrderManagementTab = false
}) => {
  // State management with memoization to prevent unnecessary re-renders
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [userBatches, setUserBatches] = useState([]);
  const [userBatchesForSearch, setUserBatchesForSearch] = useState([]);
  const [isStaff, setIsStaff] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState(orders);
  // Store current search parameters
  const [currentSearchParams, setCurrentSearchParams] = useState({
    type: null,
    field: null,
    value: null,
    status: null
  });

  // Memoize filtered orders to prevent unnecessary re-renders
  const memoizedFilteredOrders = useMemo(() => filteredOrders, [filteredOrders]);

  // Get batch operations from the BatchOperations component
  const batchOps = BatchOperations({
    selectedOrderIds,
    orders,
    onOrdersUpdate,
    onChangeCustomer
  });

  // Update filtered orders when orders prop changes
  useEffect(() => {
    setFilteredOrders(orders);
  }, [orders]);

  // Check if user is staff on component mount
  useEffect(() => {
    setIsStaff(isStaffOrSuperuser());
  }, []);

  // Handle select all checkbox
  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      const validOrders = filteredOrders.filter(order => order && order.id);
      setSelectedOrderIds(validOrders.map(order => order.id));
    } else {
      setSelectedOrderIds([]);
    }
  }, [filteredOrders]);

  // Handle individual order selection
  const handleSelect = useCallback((orderId, isSelected) => {
    if (!orderId) return;
    
    setSelectedOrderIds(prevIds => {
      if (isSelected) {
        return [...new Set([...prevIds, orderId])];
      } else {
        return prevIds.filter(id => id !== orderId);
      }
    });
  }, []);

  // Handle search results
  const handleSearchResults = useCallback((results, paginationInfo, searchParams) => {
    setFilteredOrders(results);
    if (searchParams) {
      setCurrentSearchParams(searchParams);
    }
    if (onSearchResults) {
      onSearchResults(results, paginationInfo);
    }
  }, [onSearchResults]);

  // State to store the count from the custom event
  const [statusFilterCount, setStatusFilterCount] = useState(0);
  
  // State to track pagination loading
  const [paginationLoading, setPaginationLoading] = useState(false);
  
  // Listen for the custom event
  useEffect(() => {
    const handleStatusFilterCount = (event) => {
      setStatusFilterCount(event.detail.count);
    };
    
    window.addEventListener('statusFilterCount', handleStatusFilterCount);
    
    return () => {
      window.removeEventListener('statusFilterCount', handleStatusFilterCount);
    };
  }, []);

  // Handle clearing search
  const handleClearSearch = useCallback(() => {
    setFilteredOrders(orders);
    setStatusFilterCount(0); // Reset status filter count when clearing search
    if (onClearSearch) {
      onClearSearch();
    }
  }, [orders, onClearSearch]);

  return (
    <Content style={{ 
      background: '#fff', 
      height: 'calc(100vh - 10px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <SearchFunctions
        onSearchResults={handleSearchResults}
        userBatchesForSearch={userBatchesForSearch}
        setUserBatchesForSearch={setUserBatchesForSearch}
        airwayBillId={airwayBillId}
        orders={orders}
      >
        {(searchProps) => (
          <>
            {showBatchButtons && (
              <BatchManagement
                selectedOrderIds={selectedOrderIds}
                onOrdersUpdate={onOrdersUpdate}
                userBatches={userBatches}
                setUserBatches={setUserBatches}
                airwayBillId={airwayBillId}
                toolbar={({ handleShowBatchSearch }) => (
                  <OrderListToolbar
                    selectedOrderIds={selectedOrderIds}
                    isSearchMode={isSearchMode}
                    onBatch={handleShowBatchSearch}
                    onClearSearch={handleClearSearch}
                    onDelete={batchOps.showDeleteConfirm}
                    onPrint={batchOps.handleBatchPrint}
                    onExport={batchOps.handleExport}
                    onDownloadPdf={batchOps.handleDownloadPdf}
                    onChangeCustomer={(orderIds, customerId) => {
                      onChangeCustomer(orderIds, customerId);
                      setSelectedOrderIds([]);
                    }}
                    onOrdersUpdate={onOrdersUpdate}
                    totalOrders={totalOrders}
                    orders={orders}
                    onSearchResults={handleSearchResults}
                    isMyOrdersRoute={isMyOrdersRoute}
                    isOrderManagementTab={isOrderManagementTab}
                    onSearchOrderDirect={searchProps.onSearchOrderDirect}
                    onSearchExpressDirect={searchProps.onSearchExpressDirect}
                    onSearchBatchDirect={searchProps.onSearchBatchDirect}
                    onSearchRecipientDirect={searchProps.onSearchRecipientDirect}
                    onSearchRecipientPhoneDirect={searchProps.onSearchRecipientPhoneDirect}
                    onSearchSenderDirect={searchProps.onSearchSenderDirect}
                    onSearchSenderPhoneDirect={searchProps.onSearchSenderPhoneDirect}
                    onSearchStatusDirect={searchProps.onSearchStatusDirect}
                    onSearchPrintCountDirect={searchProps.onSearchPrintCountDirect}
                    onSearchDateFilterDirect={searchProps.onSearchDateFilterDirect}
                  />
                )}
              >
                <OrderModals />
              </BatchManagement>
            )}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Header section */}
              <OrderListHeader 
                columnStyle={columnStyle}
                selectedOrderIds={selectedOrderIds}
                orders={orders}
                onSelectAll={handleSelectAll}
                onSearchOrder={searchProps.onSearchOrder}
                onSearchExpress={searchProps.onSearchExpress}
                onSearchBatch={searchProps.onSearchBatch}
                onSearchDate={searchProps.onSearchDate}
                onSearchRecipient={searchProps.onSearchRecipient}
                onSearchRecipientPhone={searchProps.onSearchRecipientPhone}
                onSearchSender={searchProps.onSearchSender}
                onSearchSenderPhone={searchProps.onSearchSenderPhone}
                onSearchStatus={searchProps.onSearchStatus}
                onSearchInspection={searchProps.onSearchInspection}
                onSearchShortPackage={searchProps.onSearchShortPackage}
                onSearchClaim={searchProps.onSearchClaim}
                onSearchModified={searchProps.onSearchModified}
                onSearchResults={handleSearchResults}
                onClearSearch={handleClearSearch}
                airwayBillId={airwayBillId}
                isStaff={isStaff}
                onSearchCustomer={searchProps.onSearchCustomer}
              />
              
              {/* Virtualized list for better performance */}
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    Loading...
                  </div>
                ) : (
                  <>
                    <VirtualizedOrderList
                      orders={memoizedFilteredOrders}
                      columnStyle={columnStyle}
                      onOrderClick={onOrderClick}
                      formatDate={formatDate}
                      getStatusColor={getStatusColor}
                      getStatusLabel={getStatusLabel}
                      onSelect={handleSelect}
                      selectedOrderIds={selectedOrderIds}
                      onOrdersUpdate={onOrdersUpdate}
                      isStaff={isStaff}
                    />
                    
                    {/* Full-screen loading overlay */}
                    {paginationLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                      }}>
                        <Spin size="large" />
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Pagination */}
              {((!isSearchMode && pagination) || isSearchMode) && (
                <div style={{
                  position: 'sticky',
                  bottom: 0,
                  background: 'white',
                  padding: '16px 0',
                  margin: 0,
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px' }}>
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                      共 {statusFilterCount || totalOrders || pagination?.total || 0} 条订单
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Loading indicator */}
                      {paginationLoading && (
                        <Spin size="small" style={{ marginRight: '10px' }} />
                      )}
                      
                      {(() => {
                        const currentPage = isSearchMode ? (searchProps.searchPagination?.page || 1) : (pagination?.current || 1);
                        const pageSize = isSearchMode ? (searchProps.searchPagination?.page_size || 100) : (pagination?.pageSize || 500);
                        const totalPages = Math.ceil((statusFilterCount || totalOrders || pagination?.total || 0) / pageSize);
                        
                        // Helper function to handle page navigation
                        const handlePageChange = (targetPage) => {
                          setPaginationLoading(true);
                          
                          if (isSearchMode) {
                            if (currentSearchParams.type === 'bool_field') {
                              axios.post('/api/orders/search_bool_field/', {
                                field: currentSearchParams.field,
                                value: currentSearchParams.value,
                                airway_bill_id: airwayBillId,
                                page: targetPage,
                                page_size: pageSize
                              }).then(response => {
                                if (response.data && response.data.results) {
                                  setFilteredOrders(response.data.results);
                                } else {
                                  setFilteredOrders([]);
                                }
                                setPaginationLoading(false);
                              }).catch(error => {
                                setPaginationLoading(false);
                              });
                            } else if (currentSearchParams.type === 'status') {
                              axios.post('/api/orders/search_status/', {
                                status: currentSearchParams.status,
                                airway_bill_id: airwayBillId,
                                page: targetPage,
                                page_size: pageSize
                              }).then(response => {
                                if (response.data && response.data.results) {
                                  setFilteredOrders(response.data.results);
                                } else {
                                  setFilteredOrders([]);
                                }
                                setPaginationLoading(false);
                              }).catch(error => {
                                setPaginationLoading(false);
                              });
                            } else {
                              const field = 'is_inspection_needed';
                              const value = true;
                              
                              axios.post('/api/orders/search_bool_field/', {
                                field,
                                value,
                                airway_bill_id: airwayBillId,
                                page: targetPage,
                                page_size: pageSize
                              }).then(response => {
                                if (response.data && response.data.results) {
                                  setFilteredOrders(response.data.results);
                                } else {
                                  setFilteredOrders([]);
                                }
                                setPaginationLoading(false);
                              }).catch(error => {
                                setPaginationLoading(false);
                              });
                            }
                          } else {
                            onPageChange && onPageChange(targetPage);
                            setPaginationLoading(false);
                          }
                        };

                        // Generate pagination numbers with ellipsis
                        const generatePaginationNumbers = () => {
                          const delta = 2; // Number of pages to show around current page
                          const range = [];
                          const rangeWithDots = [];

                          // Always include first page
                          range.push(1);

                          // Add pages around current page
                          for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                            range.push(i);
                          }

                          // Always include last page if there are multiple pages
                          if (totalPages > 1) {
                            range.push(totalPages);
                          }

                          // Remove duplicates and sort
                          const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

                          // Add ellipsis where needed
                          let prev = 0;
                          for (const page of uniqueRange) {
                            if (page - prev > 1) {
                              rangeWithDots.push('...');
                            }
                            rangeWithDots.push(page);
                            prev = page;
                          }

                          return rangeWithDots;
                        };

                        const paginationNumbers = generatePaginationNumbers();

                        return (
                          <>
                            {/* Previous button */}
                            <span 
                              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                              style={{ 
                                cursor: currentPage > 1 ? 'pointer' : 'not-allowed',
                                margin: '0 8px',
                                padding: '4px 8px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                color: currentPage > 1 ? '#1890ff' : '#d9d9d9',
                                backgroundColor: currentPage > 1 ? 'white' : '#f5f5f5'
                              }}
                            >
                              上一页
                            </span>

                            {/* Page numbers */}
                            {paginationNumbers.map((pageNum, index) => (
                              pageNum === '...' ? (
                                <span key={`ellipsis-${index}`} style={{ margin: '0 4px', color: '#999' }}>
                                  ...
                                </span>
                              ) : (
                                <span 
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{ 
                                    cursor: 'pointer', 
                                    margin: '0 4px',
                                    padding: '4px 8px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                                    color: currentPage === pageNum ? 'white' : '#1890ff',
                                    backgroundColor: currentPage === pageNum ? '#1890ff' : 'white'
                                  }}
                                >
                                  {pageNum}
                                </span>
                              )
                            ))}

                            {/* Next button */}
                            <span 
                              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                              style={{ 
                                cursor: currentPage < totalPages ? 'pointer' : 'not-allowed',
                                margin: '0 8px',
                                padding: '4px 8px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                color: currentPage < totalPages ? '#1890ff' : '#d9d9d9',
                                backgroundColor: currentPage < totalPages ? 'white' : '#f5f5f5'
                              }}
                            >
                              下一页
                            </span>

                            {/* Page info */}
                            <span style={{ margin: '0 16px', color: '#666', fontSize: '14px' }}>
                              第 {currentPage} 页 / 共 {totalPages} 页
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modals */}
            <OrderModals {...searchProps} />
            
            {/* Download Modal */}
            <DownloadModal
              visible={batchOps.downloadModalVisible}
              onOk={batchOps.handleConfirmDownload}
              onCancel={() => batchOps.setDownloadModalVisible(false)}
              isGenerating={batchOps.isGenerating}
              progress={batchOps.downloadProgress}
            />
          </>
        )}
      </SearchFunctions>
    </Content>
  );
};

export default React.memo(OrderList);
